const PROJECTS = [
  {
    slug: 'artemis-agent-optimization',
    title: 'ARTEMIS — Evolving LLM Agents',
    status: 'Research demonstrator',
    description:
      'A no-code evolutionary optimisation platform that automatically configures, tunes, and evaluates LLM-based agents, from coding agents to teaching agents, without changing their underlying architecture.',
    tech: 'LLM Agents, Evolutionary Optimisation, Multi-Agent Systems',
    metrics: '13.6% Higher Acceptance Rate • 36.9% Fewer Tokens • 22% Accuracy Gain',
    image:
      'https://images.unsplash.com/photo-1774901128215-3549cc686921?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    link: 'https://arxiv.org/abs/2512.09108',
    caseStudy: {
      datePublished: '2026-09-18',
      dateModified: '2026-09-18',
      excerpt:
        'ARTEMIS automatically evolves how LLM-based agents are configured, closing the gap between an agent that works in a demo and one that holds up in production.',
      role: 'Co-author, AI Research Engineer',
      sector: 'Agentic AI · LLM Systems',
      overview:
        'Most LLM-based agents ship with hand-tuned prompts, tool choices, and control flow that are rarely revisited once they work well enough. ARTEMIS treats that configuration space as something to search rather than guess: given only a benchmark script and a natural-language goal, it evolves agent configurations end to end using semantically-aware genetic operators, with no architectural changes required.',
      problem: [
        'Agent configurations — prompts, tool choices, control flow — are typically tuned by hand and rarely revisited once "good enough".',
        'Teams lack a systematic way to trade off cost, latency, and accuracy across an agent’s many configurable components.',
        'Improvements found for one agent rarely transfer to another without repeating the manual tuning process from scratch.'
      ],
      approach: [
        'Built ARTEMIS, a no-code evolutionary optimisation platform that discovers an agent’s configurable components automatically from its code and execution logs.',
        'Extracted performance signals directly from those logs, then used semantically-aware genetic operators to evolve configurations against a stated goal.',
        'Validated the approach across multiple real agent systems — coding, maths-teaching, and general-purpose agents — without modifying their underlying architecture.'
      ],
      outcomes: [
        '13.6% improvement in acceptance rate for the ALE coding agent, plus a 10.1% performance gain for the Mini-SWE agent.',
        '36.9% reduction in token usage for a CrewAI-based agent, with accuracy held steady.',
        '22% accuracy improvement for a maths-teaching agent evaluated on real tutoring tasks.'
      ]
    }
  },
  {
    slug: 'amara-development',
    title: 'Amara Development',
    status: 'Live product',
    description:
      'A property operation and maintenance platform for real estate in Saudi Arabia. Clients use the web platform and mobile app to track cleaning and maintenance work with photo and video documentation, manage payments and invoices, and submit urgent requests, with a focus on quality, transparency, and asset sustainability.',
    tech: 'PropTech, Facility Management, Mobile App, Payments',
    metrics: 'Operation & Maintenance • Property Management • iOS & Android App',
    image:
      'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    link: 'https://amaradev.com/',
    caseStudy: {
      datePublished: '2026-07-19',
      dateModified: '2026-07-19',
      excerpt:
        'How Amara turned fragmented property operations in Saudi Arabia into a single platform for maintenance tracking, documentation, payments, and urgent requests.',
      role: 'Founder / Product & Platform',
      sector: 'PropTech · Saudi Arabia',
      overview:
        'Property owners and operators in Saudi Arabia often juggle cleaning, maintenance, invoices, and urgent issues across WhatsApp threads, spreadsheets, and disconnected vendors. Amara was built to give them one system of record — on web and mobile — so work is visible, documented, and paid for without losing accountability.',
      problem: [
        'Operation and maintenance work was hard to verify after the fact: owners could not reliably see what was done, when, or to what standard.',
        'Payments and invoices lived apart from the work itself, creating disputes and delayed settlements.',
        'Urgent requests competed with routine work in the same informal channels, so priority and response quality varied widely.'
      ],
      approach: [
        'Designed an integrated property operations platform spanning web and native mobile (iOS & Android), so field teams and clients share the same live view of assets and jobs.',
        'Built photo and video documentation into the workflow so cleaning and maintenance are evidenced at the point of delivery, not reconstructed later.',
        'Connected payments and invoicing to completed work, and added urgent-request flows so exceptions are tracked with the same transparency as planned jobs.'
      ],
      outcomes: [
        'A live product (amaradev.com) covering operation & maintenance and property management for Saudi real estate.',
        'Client-facing transparency: documented work, invoices, and urgent requests in one place.',
        'Mobile coverage for teams and clients on iOS and Android, supporting day-to-day site operations.'
      ]
    }
  },
  {
    title: 'Space Oyster',
    status: 'Prototype',
    description:
      'A hot-desking marketplace that helps premises owners rent spare desks and rooms. Built the platform so small businesses can list space, take bookings, and make better use of underused locations.',
    tech: 'Workspace Marketplace, Booking, Small Business',
    metrics: 'Spare Space Listings • Small Business Onboarding • Improved Space Utilisation',
    image:
      'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  },
  {
    title: 'OneScan — Skin Health Companion',
    status: 'Prototype',
    description:
      'A medical imaging app for everyday skin health checks. Trained on 1M+ medical images to detect and analyse 12 skin lesion types, designed as a daily companion rather than a one-off screening tool.',
    tech: 'Medical Imaging, Dermatology, Mobile Health',
    metrics: '1M+ Training Images • 12 Lesion Types • Daily Health Monitoring',
    image:
      'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  },
  {
    title: 'Parachute — MENA Internship Platform',
    status: 'Prototype',
    description:
      'An internship discovery platform for young professionals across the MENA region. Matches candidates with opportunities using ranking models and real-time updates, aimed at making regional talent pipelines easier to navigate.',
    tech: 'Talent Matching, Internships, MENA',
    metrics: 'MENA Region Coverage • Candidate–Role Matching • Youth Employment Focus',
    image:
      'https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  },
  {
    title: 'Funds Unlocked — SME Capital Platform',
    status: 'Prototype',
    description:
      'A fintech platform that helps micro and small enterprises access capital. Built risk assessment and allocation workflows so lenders can review applications more consistently across emerging markets.',
    tech: 'Fintech, SME Lending, Risk Assessment',
    metrics: 'SME Focus • Risk Assessment Workflows • Emerging Markets',
    image:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  },
  {
    slug: 'intelligent-enterprise-search',
    title: 'Intelligent Enterprise Search',
    status: 'Prototype',
    description:
      'A Neo4j-based knowledge discovery platform piloted with Fortune 500 companies, built prior to the rise of LLMs. Helps teams find prior work and organisational knowledge faster, so projects are less likely to be rebuilt from scratch.',
    tech: 'Neo4j, Graph Databases, Enterprise Search',
    metrics: 'Fortune 500 Pilot • Knowledge Discovery • Faster Information Access',
    image:
      'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    caseStudy: {
      datePublished: '2026-07-19',
      dateModified: '2026-07-19',
      excerpt:
        'Building a graph-based enterprise search platform so large organisations can find institutional knowledge faster and stop reinventing work that already exists.',
      role: 'Senior Data Scientist → Lead Data Scientist',
      sector: 'Enterprise Knowledge · Fortune 500',
      overview:
        'In large organisations, the cost of not finding the right document, person, or past decision is invisible but enormous: duplicated projects, slow onboarding, and decisions made without the evidence that already exists inside the company. Built on Neo4j prior to the rise of LLMs, this work focused on turning fragmented enterprise content into a navigable knowledge graph that people could search and traverse.',
      problem: [
        'Critical knowledge was scattered across repositories, wikis, shared drives, and tribal memory — keyword search alone rarely surfaced the right context.',
        'Teams repeatedly rebuilt solutions that peers had already delivered elsewhere in the organisation.',
        'Documentation discovery was slow enough that people defaulted to asking around rather than trusting search.'
      ],
      approach: [
        'Designed and built a graph-based knowledge discovery platform on Neo4j that modelled relationships between documents, topics, and organisational context — not just flat text indexes.',
        'Focused the product on enterprise search and discovery workflows that reduce time-to-answer for practitioners who need prior art before starting new work.',
        'Took the prototype through pilots at client organisations, including Fortune 500 environments, iterating on relevance and adoption with real usage patterns.'
      ],
      outcomes: [
        'Piloted a Neo4j-based enterprise search prototype with Fortune 500 client organisations.',
        'Reduced documentation discovery time and helped teams avoid redundant work.',
        'Informed later product direction at TheAIWorks (including intelligent search becoming a core feature) and supported knowledge-access improvements at scale (user reach 10M+ in related platform contexts).'
      ]
    }
  },
  {
    slug: 'artificial-ecosystem-algorithm',
    title: 'Artificial Ecosystem Algorithm',
    status: 'Research demonstrator',
    description:
      "PhD research on a bio-inspired optimisation method for complex logistics. Applied to London's bike-sharing redistribution and multi-line steel production scheduling at ArcelorMittal.",
    tech: 'Evolutionary Computing, Optimization, Logistics',
    metrics: 'TfL Bike Redistribution • Multi-Line Steel Scheduling • Peer-Reviewed Research',
    image:
      'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    caseStudy: {
      datePublished: '2026-07-19',
      dateModified: '2026-07-19',
      excerpt:
        'PhD research that turned a bio-inspired optimisation method into real logistics applications — from Transport for London bike redistribution to multi-line steel scheduling at ArcelorMittal.',
      role: 'PhD Researcher, UCL · Applied with industry partners',
      sector: 'Optimisation · Logistics · Manufacturing',
      overview:
        'Many real-world logistics and scheduling problems are too large, dynamic, and constrained for brittle exact solvers. The Artificial Ecosystem Algorithm (AEA) was developed as a bio-inspired optimisation approach and validated on problems that matter in the field: balancing London’s cycle-hire network and scheduling production across multiple steel lines.',
      problem: [
        'Transport for London’s cycle scheme needed dynamic redistribution strategies so bikes and docks stayed usable across a city-scale network that changes through the day.',
        'ArcelorMittal’s multi-line steel scheduling combined conflicting objectives, capacity limits, and decomposition choices that made naïve optimisation unusable at operational scale.',
        'Existing methods struggled to stay useful when problem size grew or when the environment shifted — a common failure mode for industrial optimisation.'
      ],
      approach: [
        'Developed and evaluated the Artificial Ecosystem Algorithm as a bio-inspired optimiser, including fitness-function design and behaviour under static and dynamic problem formulations.',
        'Applied AEA to bicycle redistribution in London in collaboration with Transport for London–relevant research settings, studying how ecosystem-inspired agents could rebalance supply.',
        'Extended the method to multi-line steel scheduling, including decomposition strategies that made large industrial instances tractable, with results published at IEEE CEC, SSCI, and related venues.'
      ],
      outcomes: [
        'Peer-reviewed publications across GECCO, IEEE SSCI, IEEE CEC, PPSN, and related venues — contributing to a body of work with 60+ citations.',
        'Demonstrated applied impact on TfL-scale bike redistribution and ArcelorMittal multi-line steel scheduling.',
        'Established a research-to-industry pattern later carried into enterprise AI roles: algorithms judged by whether they survive real constraints, not only benchmark scores.'
      ]
    }
  }
];

export default PROJECTS;
